import React, { useEffect, useState, useMemo } from "react";
import { useSelector } from "react-redux";

import { isNonEmptyArray, isNonEmptyString, isNumber } from "@cayuse/shared-react.utils.type-checks";

import FeedTable from "components/FeedTable";
import SPLink from "components/SPLink";
import StartNewSubaward from "components/StartNewSubaward";
import { SUBAWARDS_BASE_ROUTE, COLUMN_HEADER_KEYS, MODIFICATION_DRAFT } from "globals/constants";
import { useDictionary, useSelectedRows, useColumnHeaders, useActiveColumns, useFormAccesses } from "hooks";
import useSimplePagination from "hooks/useSimplePagination";
import { findSubawards } from "store/actions/subawardList";
import { subawardListSelectors } from "store/selectors";
import { findAwards, getKeysFromColumnHeaders } from "utils";

import styles from "./styles.css";

const ProjectSubawardsView = ({ projectId }) => {
	const { pageLimit, pageOffset, setPageLimit, setPageNumber } = useSimplePagination();
	const [ rows, setRows ] = useState([]);
	const [ totalAwardsItems, setTotalAwardsItems ] = useState([]);
	const getRowKey = useMemo(() => row => row._item.id, []);
	const { selectedRows, isSelectAll, setRowSelection } = useSelectedRows({ rows, getRowKey });
	const formAccesses = useFormAccesses("subaward");
	const dictionary = useDictionary("subawards");

	const { items, totalItems, isFetching, isRefresh } = useSelector(subawardListSelectors.content);
	const { columnHeaders: formColumnHeaders } = useColumnHeaders(COLUMN_HEADER_KEYS.SUBAWARD);
	const { columns: activeColumns } = useActiveColumns("SUBAWARD", formColumnHeaders);

	const hasItems = isNonEmptyArray(items);

	const requiredFieldKeys = useMemo(() => getKeysFromColumnHeaders(activeColumns), [ activeColumns ]);

	const onPageChange = pageNumber => setPageNumber(pageNumber);

	const onPageLimitChange = pageLimit => setPageLimit(pageLimit);

	useEffect(() => {
		const updatedRows = hasItems
			? items.map(item => {
				const isSubawardNumberLinkable = isNonEmptyString(item.subawardId) && isNonEmptyString(item.subawardNumber);
				const linkedSubawardNumber = isSubawardNumberLinkable
					? <SPLink to={`${SUBAWARDS_BASE_ROUTE}/${item.subawardId}`}>{item.subawardNumber}</SPLink>
					: "";

				(item.subRows ?? []).forEach(subRow => {
					if (subRow.modificationStatus === MODIFICATION_DRAFT) {
						subRow.subawardNumber = <span>{subRow.subawardNumber} <i>(Draft)</i></span>;
					}
				});

				return {
					_isSelected: selectedRows.has(item.id),
					_item: item,
					...item,
					subawardNumber: linkedSubawardNumber
				};
			})
			: [];

		setRows(updatedRows);
	}, [ items, selectedRows, hasItems ]);

	useEffect(() => {
		const getAwardsCount = async () => {
			const { totalItems } = await findAwards({
				pageLimit,
				pageOffset,
				searchTerms: { projectId },
				fieldKeys: requiredFieldKeys
			});
			setTotalAwardsItems(totalItems);
		};
		if (projectId) {
			findSubawards({
				pageLimit,
				pageOffset,
				searchTerms: { projectId },
				fieldKeys: requiredFieldKeys
			});
			if(!isNumber(totalAwardsItems)) {
				getAwardsCount();
			}
		}
	}, [ pageLimit, pageOffset, projectId, requiredFieldKeys, totalAwardsItems ]);

	return (
		<>
			{formAccesses.subawardCreate && <div className={styles.startNewButton}>
				<StartNewSubaward
					key="startNewSubaward"
					fromProject
					projectHasAwards={totalAwardsItems > 0}
					projectId={projectId}
				/>
			</div>}
			<FeedTable
				isLoading={isFetching}
				isRefresh={isRefresh}
				headers={activeColumns}
				rows={rows}
				totalItems={totalItems}
				pageLimit={pageLimit}
				pageOffset={pageOffset}
				onPageChange={onPageChange}
				onPageLimitChange={onPageLimitChange}
				onRowSelectionChange={({ row, isSelected }) => setRowSelection(row, isSelected)}
				isSelectable
				isExpandable
				isSelectAll={isSelectAll}
				accessibilityLabel={({ projectTitle, _item }) => (
				`${projectTitle}, ${dictionary.accessibilityLabelSubawardNumber || "Subaward #"}${_item.subawardNumber}`
				)}
				noResultsLabel={dictionary.noResults || "No Saved Subawards"}
				captionText={dictionary.captionText || "Project Subawards"}
			/>
		</>
	);
};

export default ProjectSubawardsView;
